import { ApiResponse, ListResult } from "../VolunteersHighlightContext/constants";

export const VOLUNTEERS_GALLERY_LIST_PATH = "/volunteers-gallery";

export const getVolunteersGalleryEditPath = (galleryId: string) => `${VOLUNTEERS_GALLERY_LIST_PATH}/edit/${galleryId}`;

// Where a gallery is shown on the volunteers site: the home page has one gallery, each project can have one.
export enum VolunteersGalleryContextType {
	MainPage = 1,
	Event = 2,
}

export const volunteersGalleryContextTypeLabels: Record<VolunteersGalleryContextType, string> = {
	[VolunteersGalleryContextType.MainPage]: "Ana Sayfa",
	[VolunteersGalleryContextType.Event]: "Proje",
};

/* -------------- Rules -------------- */

const MEGABYTE = 1024 * 1024;

export const VolunteersGalleryRules = {
	// The backend validator's limit for pictures.
	pictureMaxFileSize: 5 * MEGABYTE,
	// The backend validator allows 50 MB, but the server refuses any request body over 30,000,000 bytes (the
	// Kestrel/IIS default, not raised for these endpoints), so a video over about 28.6 MB can never arrive.
	videoMaxFileSize: 28 * MEGABYTE,
	// The backend accepts at most 30 files in one request; one save uploads at most that many.
	maxFileCount: 30,
	maxRequestBytes: 30_000_000,
	pictureExtensions: ["jpg", "jpeg", "png", "gif"],
	videoExtensions: ["mp4"],
} as const;

export type GalleryMediaKind = "picture" | "video";

/* -------------- LIST (response) -------------- */

export interface VolunteersGalleryListItem {
	id: string;
	contextType: VolunteersGalleryContextType;
	// The project of a project gallery; null for the home page gallery.
	contextId: string | null;
	isActive: boolean;
	itemCount: number;
}

export type VolunteersGalleryListApiResponse = ApiResponse<ListResult<VolunteersGalleryListItem>>;

/* -------------- GET (response) -------------- */

export interface VolunteersGalleryItem {
	id: string;
	galleryId: string;
	// The full address of the picture or the MP4 video; the extension tells which.
	image: string | null;
	displayOrder: number;
	isActive: boolean;
}

export interface VolunteersGalleryDetail {
	id: string;
	contextType: VolunteersGalleryContextType;
	contextId: string | null;
	isActive: boolean;
	// Sorted by display order (1, 2, 3...).
	items: VolunteersGalleryItem[];
}

export type VolunteersGalleryGetApiResponse = ApiResponse<VolunteersGalleryDetail>;

/* -------------- ADD (form = request) -------------- */

export interface VolunteersGalleryFormData {
	contextType: VolunteersGalleryContextType | null;
	contextId: string | null;
	isActive: boolean;
	files: File[];
}

export const getDefaultVolunteersGalleryFormData = (): VolunteersGalleryFormData => ({
	contextType: null,
	contextId: null,
	isActive: false,
	files: [],
});

/* -------------- UPDATE (requests) -------------- */

export interface UpdateVolunteersGalleryRequest {
	galleryId: string;
	isActive: boolean;
}

// Only the given fields change; a new display order moves the item there and the server shifts the others.
export interface UpdateVolunteersGalleryItemRequest {
	itemId: string;
	image?: File;
	displayOrder?: number;
	isActive?: boolean;
}

/* -------------- Projects -------------- */

// A project that a gallery can belong to, from the project list of the CMS.
export interface GalleryEventOption {
	eventId: string;
	header: string;
	isActive: boolean;
}

/* -------------- Texts -------------- */

export const VolunteersGalleryTexts = {
	notFound: "Galeri bulunamadı.",
	unknownEvent: "Bulunamayan proje",
	noChange: "Herhangi bir değişiklik yapılmadı.",
};

// The backend sends its gallery messages as untranslated keys ("[VOLUNTEERS_GALLERY_NOT_FOUND]").
export const VOLUNTEERS_GALLERY_ERROR_MESSAGES: Record<string, string> = {
	VOLUNTEERS_GALLERY_NOT_FOUND: "Galeri bulunamadı.",
	VOLUNTEERS_GALLERY_ALREADY_EXISTS: "Bu yer için zaten bir galeri var.",
	VOLUNTEERS_GALLERY_EVENT_NOT_FOUND: "Seçilen proje bulunamadı.",
	VOLUNTEERS_GALLERY_LIST_ERROR: "Galeriler alınırken bir hata oluştu.",
	VOLUNTEERS_GALLERY_GET_ERROR: "Galeri alınırken bir hata oluştu.",
	VOLUNTEERS_GALLERY_ADD_ERROR: "Galeri eklenirken bir hata oluştu.",
	VOLUNTEERS_GALLERY_UPDATE_ERROR: "Galeri güncellenirken bir hata oluştu.",
	VOLUNTEERS_GALLERY_DELETE_ERROR: "Galeri silinirken bir hata oluştu.",
	VOLUNTEERS_GALLERY_ITEM_NOT_FOUND: "Galeri öğesi bulunamadı.",
	VOLUNTEERS_GALLERY_ITEM_ADD_ERROR: "Dosyalar yüklenirken bir hata oluştu.",
	VOLUNTEERS_GALLERY_ITEM_UPDATE_ERROR: "Galeri öğesi güncellenirken bir hata oluştu.",
	VOLUNTEERS_GALLERY_ITEM_DELETE_ERROR: "Galeri öğesi silinirken bir hata oluştu.",
	VOLUNTEERS_GALLERY_REQUIRED: "Galeri bilgisi eksik.",
	VOLUNTEERS_GALLERY_CONTEXT_TYPE_INVALID: "Geçersiz galeri yeri.",
	VOLUNTEERS_GALLERY_CONTEXT_ID_REQUIRED: "Lütfen bir proje seçin.",
	VOLUNTEERS_GALLERY_CONTEXT_ID_MUST_BE_EMPTY: "Ana sayfa galerisi için proje seçilmemelidir.",
	VOLUNTEERS_GALLERY_ITEM_REQUIRED: "Galeri öğesi bilgisi eksik.",
	VOLUNTEERS_GALLERY_ITEM_IMAGE_REQUIRED: "Lütfen en az bir görsel ya da video seçin.",
	VOLUNTEERS_GALLERY_ITEM_IMAGE_COUNT_EXCEEDED: `Bir seferde en fazla ${VolunteersGalleryRules.maxFileCount} dosya yüklenebilir.`,
	VOLUNTEERS_GALLERY_ITEM_DISPLAY_ORDER_INVALID: "Geçersiz sıra numarası.",
};

export const UNEXPECTED_GALLERY_ERROR = "Beklenmedik bir hata oluştu.";

// The backend's gallery messages are untranslated keys ("[VOLUNTEERS_GALLERY_NOT_FOUND]"), and its content check
// message keeps an unfilled "{0}" for the file name.
export const toGalleryErrorMessage = (text: string | undefined): string => {
	if (!text) {
		return UNEXPECTED_GALLERY_ERROR;
	}
	const key = /^\[([A-Z_]+)\]$/.exec(text.trim())?.[1];
	if (key) {
		return VOLUNTEERS_GALLERY_ERROR_MESSAGES[key] ?? UNEXPECTED_GALLERY_ERROR;
	}
	return text.replace("{0} adlı dosyanın", "Yüklenen dosyalardan birinin");
};
